import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tasksAPI, meetingsAPIExtended } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { 
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, 
  eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths,
  addWeeks, subWeeks, isToday, parseISO, isBefore, isAfter, startOfDay
} from 'date-fns';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, 
  CheckSquare, Video, Phone, MapPin, Users, RefreshCw,
  ListTodo, Target, Bell, Filter, Plus, ExternalLink
} from 'lucide-react';

// Event type configurations
const EVENT_TYPES = {
  task: { 
    color: 'bg-blue-500', 
    lightColor: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: CheckSquare,
    label: 'Task'
  },
  meeting: { 
    color: 'bg-purple-500', 
    lightColor: 'bg-purple-100 text-purple-700 border-purple-200',
    icon: Video,
    label: 'Meeting'
  },
  activity: { 
    color: 'bg-green-500', 
    lightColor: 'bg-green-100 text-green-700 border-green-200',
    icon: Bell,
    label: 'Activity'
  },
  followup: { 
    color: 'bg-amber-500', 
    lightColor: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Target,
    label: 'Follow-up'
  },
  blocker: { 
    color: 'bg-slate-500', 
    lightColor: 'bg-slate-100 text-slate-700 border-slate-200',
    icon: Clock,
    label: 'Time Block'
  }
};

// Calendar Day Cell Component
const CalendarDayCell = ({ date, events, currentMonth, onClick, isSelected }) => {
  const dayEvents = events.filter(e => {
    const eventDate = parseISO(e.date);
    return isSameDay(eventDate, date);
  });
  
  const isCurrentMonth = isSameMonth(date, currentMonth);
  const today = isToday(date);
  
  return (
    <div 
      className={`min-h-[100px] p-1 border-r border-b cursor-pointer transition-colors
        ${!isCurrentMonth ? 'bg-slate-50 text-slate-400' : 'hover:bg-slate-50'}
        ${today ? 'bg-blue-50' : ''}
        ${isSelected ? 'ring-2 ring-blue-500 ring-inset' : ''}
      `}
      onClick={() => onClick(date)}
      data-testid={`calendar-day-${format(date, 'yyyy-MM-dd')}`}
    >
      <div className={`text-sm font-medium mb-1 ${today ? 'text-blue-600' : ''}`}>
        {format(date, 'd')}
      </div>
      <div className="space-y-0.5">
        {dayEvents.slice(0, 3).map((event, idx) => (
          <div 
            key={event.id || idx}
            className={`text-xs px-1 py-0.5 rounded truncate ${EVENT_TYPES[event.type]?.lightColor || 'bg-slate-100'}`}
            title={event.title}
          >
            {event.title}
          </div>
        ))}
        {dayEvents.length > 3 && (
          <div className="text-xs text-slate-500 px-1">
            +{dayEvents.length - 3} more
          </div>
        )}
      </div>
    </div>
  );
};

// Event Card Component - Clickable to navigate to task/meeting
const EventCard = ({ event, onClick }) => {
  const config = EVENT_TYPES[event.type] || EVENT_TYPES.activity;
  const Icon = config.icon;
  
  return (
    <div 
      className={`p-3 rounded-lg border ${config.lightColor} transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer`}
      data-testid={`event-card-${event.id}`}
      onClick={() => onClick && onClick(event)}
    >
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded ${config.color} text-white`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{event.title}</p>
          {event.time && (
            <p className="text-xs flex items-center gap-1 mt-0.5 opacity-80">
              <Clock className="h-3 w-3" />
              {event.time}
            </p>
          )}
          {event.contact_name && (
            <p className="text-xs flex items-center gap-1 mt-0.5 opacity-80">
              <Users className="h-3 w-3" />
              {event.contact_name}
            </p>
          )}
          {/* Bug #2 Fix: Show assignee on calendar events */}
          {event.assigned_to_name && (
            <p className="text-xs flex items-center gap-1 mt-0.5 opacity-80">
              <Target className="h-3 w-3" />
              Assigned: {event.assigned_to_name}
            </p>
          )}
          {event.location && (
            <p className="text-xs flex items-center gap-1 mt-0.5 opacity-80">
              <MapPin className="h-3 w-3" />
              {event.location}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge variant="outline" className="text-xs shrink-0">
            {config.label}
          </Badge>
          <ExternalLink className="h-3 w-3 text-muted-foreground" />
        </div>
      </div>
    </div>
  );
};

// Weekly View Component
const WeeklyView = ({ currentDate, events, onDayClick, onEventClick }) => {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
  
  return (
    <div className="grid grid-cols-7 border-l border-t">
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
        <div key={day} className="p-2 text-center text-sm font-medium bg-slate-50 border-r border-b">
          {day}
        </div>
      ))}
      {days.map(day => {
        const dayEvents = events.filter(e => isSameDay(parseISO(e.date), day));
        return (
          <div 
            key={day.toISOString()}
            className={`min-h-[150px] p-2 border-r border-b cursor-pointer hover:bg-slate-50
              ${isToday(day) ? 'bg-blue-50' : ''}
            `}
            onClick={() => onDayClick(day)}
          >
            <div className={`text-sm font-medium mb-2 ${isToday(day) ? 'text-blue-600' : ''}`}>
              {format(day, 'MMM d')}
            </div>
            <div className="space-y-1">
              {dayEvents.map((event, idx) => (
                <EventCard key={event.id || idx} event={event} onClick={onEventClick} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Daily View Component
const DailyView = ({ currentDate, events, onEventClick }) => {
  const dayEvents = events.filter(e => isSameDay(parseISO(e.date), currentDate));
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="p-4 bg-slate-50 border-b">
        <h3 className="font-semibold">{format(currentDate, 'EEEE, MMMM d, yyyy')}</h3>
        <p className="text-sm text-muted-foreground">{dayEvents.length} events</p>
      </div>
      <ScrollArea className="h-[600px]">
        <div className="relative">
          {hours.map(hour => (
            <div key={hour} className="flex border-b min-h-[60px]">
              <div className="w-20 p-2 text-xs text-muted-foreground border-r bg-slate-50">
                {format(new Date().setHours(hour, 0), 'h:mm a')}
              </div>
              <div className="flex-1 p-2">
                {dayEvents
                  .filter(e => {
                    if (!e.time) return hour === 9; // Default to 9am for untimed events
                    const eventHour = parseInt(e.time.split(':')[0]);
                    return eventHour === hour;
                  })
                  .map((event, idx) => (
                    <EventCard key={event.id || idx} event={event} onClick={onEventClick} />
                  ))
                }
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export const CalendarPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({
    tasks: true,
    meetings: true,
    activities: true,
    followups: true
  });
  
  // Quick task creation state
  const [quickTaskOpen, setQuickTaskOpen] = useState(false);
  const [quickTaskForm, setQuickTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: format(new Date(), 'yyyy-MM-dd')
  });
  const [creatingTask, setCreatingTask] = useState(false);

  // Quick meeting creation state (Bug #13)
  const [quickMeetingOpen, setQuickMeetingOpen] = useState(false);
  const [quickMeetingForm, setQuickMeetingForm] = useState({
    title: '',
    description: '',
    meeting_type: 'call',
    scheduled_datetime: '',
    duration_minutes: 30,
    meeting_link: ''
  });
  const [creatingMeeting, setCreatingMeeting] = useState(false);

  // Time blocker state (Bug #14)
  const [timeBlockerOpen, setTimeBlockerOpen] = useState(false);
  const [timeBlockerForm, setTimeBlockerForm] = useState({
    title: '',
    start_time: '',
    end_time: '',
    all_day: false,
    color: 'gray'
  });
  const [creatingBlocker, setCreatingBlocker] = useState(false);

  // Meeting detail view state (Bug #2 fix)
  const [meetingDetailOpen, setMeetingDetailOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [meetingDetailLoading, setMeetingDetailLoading] = useState(false);

  const fetchCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, meetingsRes] = await Promise.all([
        tasksAPI.getAll(),
        meetingsAPIExtended.getUpcoming()
      ]);

      const allEvents = [];
      
      // Process tasks - include ALL tasks, not just those with due dates
      const tasks = Array.isArray(tasksRes.data) ? tasksRes.data : tasksRes.data?.tasks || [];
      tasks.forEach(task => {
        // Include tasks with due_date OR pending tasks without due_date (show on today)
        const taskDate = task.due_date || (task.status === 'pending' ? format(new Date(), 'yyyy-MM-dd') : null);
        if (taskDate) {
          // Only use related_to_id as contact_id if related_to_type is 'contact'
          const effectiveContactId = task.contact_id || 
            (task.related_to_type === 'contact' ? task.related_to_id : null);
          
          allEvents.push({
            id: task.id,
            type: task.is_follow_up ? 'followup' : 'task',
            title: task.title,
            date: taskDate,
            time: task.due_time || null,
            status: task.status,
            priority: task.priority,
            contact_name: task.contact_name || task.related_to_name,
            contact_id: effectiveContactId,
            description: task.description,
            assigned_to: task.assigned_to,
            assigned_to_name: task.assigned_to_name,
            assigned_to_type: task.assigned_to_type
          });
        }
      });

      // Process meetings
      const meetings = Array.isArray(meetingsRes.data) ? meetingsRes.data : meetingsRes.data?.meetings || [];
      meetings.forEach(meeting => {
        if (meeting.scheduled_datetime || meeting.start_time) {
          const dateStr = meeting.scheduled_datetime || meeting.start_time;
          allEvents.push({
            id: meeting.id,
            type: 'meeting',
            title: meeting.title || meeting.meeting_type_name || 'Meeting',
            date: dateStr.split('T')[0],
            time: format(parseISO(dateStr), 'h:mm a'),
            location: meeting.meeting_location || meeting.meeting_link,
            contact_name: meeting.customer_name || meeting.contact_name,
            contact_id: meeting.contact_id
          });
        }
      });

      setEvents(allEvents);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);
  
  // Handle event click - navigate to appropriate page
  const handleEventClick = useCallback(async (event) => {
    if (event.type === 'task' || event.type === 'followup') {
      // Always navigate to tasks page for consistency and to avoid orphaned contact issues
      navigate(`/tasks?taskId=${event.id}`);
    } else if (event.type === 'meeting') {
      // Bug #2 Fix: Show meeting details in a dialog instead of navigating to non-existent page
      setMeetingDetailLoading(true);
      setMeetingDetailOpen(true);
      try {
        const response = await meetingsAPIExtended.getById(event.id);
        setSelectedMeeting(response.data);
      } catch (error) {
        console.error('Failed to fetch meeting details:', error);
        // If API call fails, use the event data we already have
        setSelectedMeeting({
          id: event.id,
          title: event.title,
          scheduled_datetime: event.date + 'T' + (event.time || '09:00'),
          meeting_type: event.meeting_type || 'call',
          contact_name: event.contact_name,
          contact_id: event.contact_id,
          timezone: event.timezone
        });
      } finally {
        setMeetingDetailLoading(false);
      }
    }
  }, [navigate]);
  
  // Quick task creation
  const handleCreateQuickTask = async () => {
    if (!quickTaskForm.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    
    setCreatingTask(true);
    try {
      await tasksAPI.create({
        title: quickTaskForm.title,
        description: quickTaskForm.description,
        priority: quickTaskForm.priority,
        due_date: quickTaskForm.due_date,
        status: 'pending'
      });
      toast.success('Task created successfully');
      setQuickTaskOpen(false);
      setQuickTaskForm({
        title: '',
        description: '',
        priority: 'medium',
        due_date: format(new Date(), 'yyyy-MM-dd')
      });
      fetchCalendarData(); // Refresh calendar
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  };

  // Create meeting handler (Bug #13)
  const handleCreateMeeting = async () => {
    if (!quickMeetingForm.title) {
      toast.error('Please enter a meeting title');
      return;
    }
    if (!quickMeetingForm.scheduled_datetime) {
      toast.error('Please select a date and time');
      return;
    }
    
    setCreatingMeeting(true);
    try {
      await meetingsAPIExtended.create({
        title: quickMeetingForm.title,
        description: quickMeetingForm.description,
        meeting_type: quickMeetingForm.meeting_type,
        scheduled_datetime: quickMeetingForm.scheduled_datetime,
        duration_minutes: parseInt(quickMeetingForm.duration_minutes) || 30,
        meeting_link: quickMeetingForm.meeting_link,
        status: 'scheduled'
      });
      toast.success('Meeting scheduled successfully');
      setQuickMeetingOpen(false);
      setQuickMeetingForm({
        title: '',
        description: '',
        meeting_type: 'call',
        scheduled_datetime: '',
        duration_minutes: 30,
        meeting_link: ''
      });
      fetchCalendarData();
    } catch (error) {
      console.error('Failed to create meeting:', error);
      toast.error('Failed to schedule meeting');
    } finally {
      setCreatingMeeting(false);
    }
  };

  // Create time blocker handler (Bug #14)
  const handleCreateTimeBlocker = async () => {
    if (!timeBlockerForm.title) {
      toast.error('Please enter a title for the time block');
      return;
    }
    if (!timeBlockerForm.start_time) {
      toast.error('Please select a start time');
      return;
    }
    
    setCreatingBlocker(true);
    try {
      // Create as a special task type for time blocking
      await tasksAPI.create({
        title: `🚫 ${timeBlockerForm.title}`,
        description: `Time blocked: ${timeBlockerForm.start_time} - ${timeBlockerForm.end_time || 'End of day'}`,
        priority: 'low',
        due_date: timeBlockerForm.start_time.split('T')[0],
        status: 'in_progress',
        task_type: 'time_block',
        metadata: {
          is_time_blocker: true,
          start_time: timeBlockerForm.start_time,
          end_time: timeBlockerForm.end_time,
          all_day: timeBlockerForm.all_day,
          color: timeBlockerForm.color
        }
      });
      toast.success('Time blocked successfully');
      setTimeBlockerOpen(false);
      setTimeBlockerForm({
        title: '',
        start_time: '',
        end_time: '',
        all_day: false,
        color: 'gray'
      });
      fetchCalendarData();
    } catch (error) {
      console.error('Failed to create time block:', error);
      toast.error('Failed to block time');
    } finally {
      setCreatingBlocker(false);
    }
  };

  // Filter events based on selected filters
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      if (event.type === 'task' && !filters.tasks) return false;
      if (event.type === 'meeting' && !filters.meetings) return false;
      if (event.type === 'activity' && !filters.activities) return false;
      if (event.type === 'followup' && !filters.followups) return false;
      return true;
    });
  }, [events, filters]);

  // Get events for selected date
  const selectedDateEvents = useMemo(() => {
    if (!selectedDate) return [];
    return filteredEvents.filter(e => isSameDay(parseISO(e.date), selectedDate));
  }, [selectedDate, filteredEvents]);
  
  // Get today's events AND upcoming events (next 7 days)
  const todayAndUpcomingEvents = useMemo(() => {
    const today = startOfDay(new Date());
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return filteredEvents
      .filter(e => {
        try {
          const eventDate = parseISO(e.date);
          // Include today's events and future events within 7 days
          return !isBefore(eventDate, today) && !isAfter(eventDate, nextWeek);
        } catch {
          return false;
        }
      })
      .sort((a, b) => {
        try {
          return parseISO(a.date) - parseISO(b.date);
        } catch {
          return 0;
        }
      });
  }, [filteredEvents]);
  
  // Get only today's events
  const todayEvents = useMemo(() => {
    const today = new Date();
    return filteredEvents.filter(e => {
      try {
        return isSameDay(parseISO(e.date), today);
      } catch {
        return false;
      }
    });
  }, [filteredEvents]);

  // Navigation handlers
  const handlePrevious = () => {
    if (view === 'month') {
      setCurrentDate(prev => subMonths(prev, 1));
    } else if (view === 'week') {
      setCurrentDate(prev => subWeeks(prev, 1));
    }
  };

  const handleNext = () => {
    if (view === 'month') {
      setCurrentDate(prev => addMonths(prev, 1));
    } else if (view === 'week') {
      setCurrentDate(prev => addWeeks(prev, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    if (view === 'month') {
      // Show day events in sidebar
    }
  };

  // Generate calendar grid for month view
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] flex items-center gap-3">
            <CalendarIcon className="h-8 w-8 text-blue-600" />
            Calendar
          </h1>
          <p className="text-muted-foreground mt-1">
            View your tasks, meetings, and activities
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => setQuickTaskOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="quick-task-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Task
          </Button>
          <Button 
            onClick={() => {
              setQuickMeetingForm(prev => ({
                ...prev,
                scheduled_datetime: selectedDate 
                  ? `${format(selectedDate, 'yyyy-MM-dd')}T09:00` 
                  : `${format(new Date(), 'yyyy-MM-dd')}T09:00`
              }));
              setQuickMeetingOpen(true);
            }}
            variant="outline"
            data-testid="book-meeting-btn"
          >
            <Video className="h-4 w-4 mr-1" />
            Book Meeting
          </Button>
          <Button 
            onClick={() => {
              setTimeBlockerForm(prev => ({
                ...prev,
                start_time: selectedDate 
                  ? `${format(selectedDate, 'yyyy-MM-dd')}T09:00` 
                  : `${format(new Date(), 'yyyy-MM-dd')}T09:00`
              }));
              setTimeBlockerOpen(true);
            }}
            variant="outline"
            data-testid="time-blocker-btn"
          >
            <Clock className="h-4 w-4 mr-1" />
            Block Time
          </Button>
          <Button variant="outline" size="sm" onClick={handleToday}>
            Today
          </Button>
          <div className="flex items-center border rounded-lg">
            <Button variant="ghost" size="icon" onClick={handlePrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-4 font-medium min-w-[180px] text-center">
              {view === 'month' && format(currentDate, 'MMMM yyyy')}
              {view === 'week' && `Week of ${format(startOfWeek(currentDate), 'MMM d')}`}
              {view === 'day' && format(currentDate, 'MMMM d, yyyy')}
            </span>
            <Button variant="ghost" size="icon" onClick={handleNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={fetchCalendarData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* View Tabs and Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="month">Month</TabsTrigger>
            <TabsTrigger value="week">Week</TabsTrigger>
            <TabsTrigger value="day">Day</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          {Object.entries(EVENT_TYPES).map(([key, config]) => (
            <Button
              key={key}
              variant={filters[key + 's'] ? 'default' : 'outline'}
              size="sm"
              className={filters[key + 's'] ? config.color : ''}
              onClick={() => setFilters(prev => ({ ...prev, [key + 's']: !prev[key + 's'] }))}
            >
              <config.icon className="h-3 w-3 mr-1" />
              {config.label}s
            </Button>
          ))}
        </div>
      </div>

      {/* Calendar Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Calendar */}
        <div className="lg:col-span-3">
          <Card>
            <CardContent className="p-0">
              {view === 'month' && (
                <div className="overflow-hidden rounded-lg">
                  {/* Week day headers */}
                  <div className="grid grid-cols-7 border-l border-t">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="p-2 text-center text-sm font-medium bg-slate-50 border-r border-b">
                        {day}
                      </div>
                    ))}
                  </div>
                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 border-l">
                    {calendarDays.map(day => (
                      <CalendarDayCell
                        key={day.toISOString()}
                        date={day}
                        events={filteredEvents}
                        currentMonth={currentDate}
                        onClick={handleDayClick}
                        isSelected={selectedDate && isSameDay(day, selectedDate)}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {view === 'week' && (
                <WeeklyView 
                  currentDate={currentDate} 
                  events={filteredEvents} 
                  onDayClick={handleDayClick}
                  onEventClick={handleEventClick}
                />
              )}
              
              {view === 'day' && (
                <DailyView 
                  currentDate={selectedDate || currentDate} 
                  events={filteredEvents}
                  onEventClick={handleEventClick}
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Today's Schedule & Upcoming */}
        <div className="lg:col-span-1 space-y-4">
          {/* Today's Events */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <ListTodo className="h-5 w-5 text-blue-600" />
                {selectedDate ? format(selectedDate, 'MMM d') : "Today's"} Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[250px]">
                {(selectedDate ? selectedDateEvents : todayEvents).length > 0 ? (
                  <div className="space-y-3 pr-2">
                    {(selectedDate ? selectedDateEvents : todayEvents).map((event, idx) => (
                      <EventCard key={event.id || idx} event={event} onClick={handleEventClick} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <CalendarIcon className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No events for {selectedDate ? format(selectedDate, 'MMM d') : 'today'}</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-amber-600" />
                Upcoming (Next 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px]">
                {todayAndUpcomingEvents.length > 0 ? (
                  <div className="space-y-2 pr-2">
                    {todayAndUpcomingEvents.slice(0, 10).map((event, idx) => {
                      const config = EVENT_TYPES[event.type] || EVENT_TYPES.activity;
                      const Icon = config.icon;
                      const eventDate = parseISO(event.date);
                      const isEventToday = isSameDay(eventDate, new Date());
                      
                      return (
                        <div 
                          key={event.id || idx}
                          className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => handleEventClick(event)}
                        >
                          <div className={`p-1 rounded ${config.color} text-white`}>
                            <Icon className="h-3 w-3" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{event.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {isEventToday ? 'Today' : format(eventDate, 'EEE, MMM d')}
                              {event.time && ` • ${event.time}`}
                            </p>
                          </div>
                          <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <Target className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No upcoming events</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">This Month</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(EVENT_TYPES).map(([key, config]) => {
                  const count = filteredEvents.filter(e => {
                    try {
                      return e.type === key && isSameMonth(parseISO(e.date), currentDate);
                    } catch {
                      return false;
                    }
                  }).length;
                  return (
                    <div key={key} className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm">
                        <config.icon className="h-4 w-4" />
                        {config.label}s
                      </span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Task Creation Dialog */}
      <Dialog open={quickTaskOpen} onOpenChange={setQuickTaskOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-600" />
              Add Quick Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="task-title">Task Title *</Label>
              <Input
                id="task-title"
                placeholder="Enter task title..."
                value={quickTaskForm.title}
                onChange={(e) => setQuickTaskForm(prev => ({ ...prev, title: e.target.value }))}
                data-testid="quick-task-title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-desc">Description</Label>
              <Textarea
                id="task-desc"
                placeholder="Add details..."
                rows={3}
                value={quickTaskForm.description}
                onChange={(e) => setQuickTaskForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={quickTaskForm.due_date}
                  onChange={(e) => setQuickTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={quickTaskForm.priority}
                  onValueChange={(value) => setQuickTaskForm(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickTaskOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateQuickTask} 
              disabled={creatingTask || !quickTaskForm.title.trim()}
              data-testid="quick-task-submit"
            >
              {creatingTask ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Book Meeting Dialog (Bug #13) */}
      <Dialog open={quickMeetingOpen} onOpenChange={setQuickMeetingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-purple-600" />
              Book a Meeting
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="meeting-title">Meeting Title *</Label>
              <Input
                id="meeting-title"
                placeholder="Enter meeting title..."
                value={quickMeetingForm.title}
                onChange={(e) => setQuickMeetingForm(prev => ({ ...prev, title: e.target.value }))}
                data-testid="meeting-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="meeting-desc">Description</Label>
              <Textarea
                id="meeting-desc"
                placeholder="Meeting agenda..."
                rows={2}
                value={quickMeetingForm.description}
                onChange={(e) => setQuickMeetingForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={quickMeetingForm.scheduled_datetime}
                  onChange={(e) => setQuickMeetingForm(prev => ({ ...prev, scheduled_datetime: e.target.value }))}
                  data-testid="meeting-datetime-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Select
                  value={String(quickMeetingForm.duration_minutes)}
                  onValueChange={(value) => setQuickMeetingForm(prev => ({ ...prev, duration_minutes: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Meeting Type</Label>
                <Select
                  value={quickMeetingForm.meeting_type}
                  onValueChange={(value) => setQuickMeetingForm(prev => ({ ...prev, meeting_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="video">Video Call</SelectItem>
                    <SelectItem value="in_person">In Person</SelectItem>
                    <SelectItem value="demo">Demo</SelectItem>
                    <SelectItem value="interview">Interview</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Meeting Link</Label>
                <Input
                  placeholder="https://zoom.us/..."
                  value={quickMeetingForm.meeting_link}
                  onChange={(e) => setQuickMeetingForm(prev => ({ ...prev, meeting_link: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickMeetingOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateMeeting} 
              disabled={creatingMeeting || !quickMeetingForm.title.trim() || !quickMeetingForm.scheduled_datetime}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="create-meeting-btn"
            >
              {creatingMeeting ? 'Scheduling...' : 'Schedule Meeting'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Blocker Dialog (Bug #14) */}
      <Dialog open={timeBlockerOpen} onOpenChange={setTimeBlockerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-slate-600" />
              Block Time
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="blocker-title">Block Title *</Label>
              <Input
                id="blocker-title"
                placeholder="e.g., Focus Time, Lunch, Personal"
                value={timeBlockerForm.title}
                onChange={(e) => setTimeBlockerForm(prev => ({ ...prev, title: e.target.value }))}
                data-testid="blocker-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="datetime-local"
                  value={timeBlockerForm.start_time}
                  onChange={(e) => setTimeBlockerForm(prev => ({ ...prev, start_time: e.target.value }))}
                  data-testid="blocker-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="datetime-local"
                  value={timeBlockerForm.end_time}
                  onChange={(e) => setTimeBlockerForm(prev => ({ ...prev, end_time: e.target.value }))}
                  data-testid="blocker-end-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <Select
                value={timeBlockerForm.color}
                onValueChange={(value) => setTimeBlockerForm(prev => ({ ...prev, color: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gray">Gray (Default)</SelectItem>
                  <SelectItem value="red">Red (Busy)</SelectItem>
                  <SelectItem value="blue">Blue (Focus)</SelectItem>
                  <SelectItem value="green">Green (Personal)</SelectItem>
                  <SelectItem value="purple">Purple (Meeting Prep)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTimeBlockerOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTimeBlocker} 
              disabled={creatingBlocker || !timeBlockerForm.title.trim() || !timeBlockerForm.start_time}
              data-testid="create-blocker-btn"
            >
              {creatingBlocker ? 'Blocking...' : 'Block Time'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meeting Detail Dialog (Bug #2 Fix) */}
      <Dialog open={meetingDetailOpen} onOpenChange={setMeetingDetailOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-purple-600" />
              Meeting Details
            </DialogTitle>
          </DialogHeader>
          {meetingDetailLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : selectedMeeting ? (
            <div className="space-y-4" data-testid="meeting-detail-content">
              <div>
                <h3 className="text-lg font-semibold">{selectedMeeting.title}</h3>
                <Badge variant="outline" className="mt-1">
                  {selectedMeeting.meeting_type || 'Meeting'}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Date & Time</Label>
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      {selectedMeeting.scheduled_datetime 
                        ? format(parseISO(selectedMeeting.scheduled_datetime), 'MMM d, yyyy h:mm a')
                        : 'Not scheduled'}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Duration</Label>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{selectedMeeting.duration_minutes || 30} minutes</span>
                  </div>
                </div>
              </div>

              {selectedMeeting.contact_name && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Attendee</Label>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{selectedMeeting.contact_name}</span>
                    {selectedMeeting.contact_id && (
                      <Link 
                        to={`/contacts/${selectedMeeting.contact_id}`}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        View Contact
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {selectedMeeting.customer_email && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Email</Label>
                  <p className="text-sm">{selectedMeeting.customer_email}</p>
                </div>
              )}

              {selectedMeeting.description && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Description</Label>
                  <p className="text-sm text-muted-foreground">{selectedMeeting.description}</p>
                </div>
              )}

              {selectedMeeting.meeting_link && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Meeting Link</Label>
                  <a 
                    href={selectedMeeting.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Join Meeting
                  </a>
                </div>
              )}

              {selectedMeeting.timezone && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Timezone</Label>
                  <p className="text-sm text-muted-foreground">{selectedMeeting.timezone}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">No meeting details available</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMeetingDetailOpen(false)}>
              Close
            </Button>
            {selectedMeeting?.contact_id && (
              <Button 
                onClick={() => {
                  setMeetingDetailOpen(false);
                  navigate(`/contacts/${selectedMeeting.contact_id}`);
                }}
              >
                View Contact
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
